import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { selectIsAuthenticated, selectUser, setAuth } from './store/slices/authSlice';
import { setCompany } from './store/slices/companySlice';
import { setOwnedCompanies, setActiveCompanyId } from './store/slices/workspaceSlice';
import { selectThemeMode } from './store/slices/themeSlice';
import { authApi } from './api/axios';
import { useEffect, useState } from 'react';

// Auth layout & pages
import AuthLayout from './app/(auth)/layout';
import LoginPage from './app/(auth)/login/page';
import SelectPlanPage from './app/(auth)/select-plan/page';

// Dashboard layout & pages
import DashboardLayout from './app/(dashboard)/layout';
import DashboardPage from './app/(dashboard)/dashboard/page';
import EmployeesPage from './app/(dashboard)/employees/page';
import EmployeesNewPage from './app/(dashboard)/employees/new/page';
import EmployeeDetailPage from './app/(dashboard)/employees/[id]/page';
import EmployeeEditPage from './app/(dashboard)/employees/[id]/edit/page';
import IdCardsPage from './app/(dashboard)/employees/id-cards/page';
import AttendancePage from './app/(dashboard)/attendance/page';
import AttendanceDailyPage from './app/(dashboard)/attendance/daily/page';
import AttendanceMonthlyPage from './app/(dashboard)/attendance/monthly/page';
import AttendanceQrPage from './app/(dashboard)/attendance/qr/page';
import PayrollPage from './app/(dashboard)/payroll/page';
import PayrollRunPage from './app/(dashboard)/payroll/run/page';
import PayrollHistoryPage from './app/(dashboard)/payroll/history/page';
import PayrollRunDetailPage from './app/(dashboard)/payroll/[runId]/page';
import SalaryStructuresPage from './app/(dashboard)/payroll/salary-structures/page';
import AdvancesPage from './app/(dashboard)/payroll/advances/page';
import LeavePage from './app/(dashboard)/leave/page';
import LeaveRequestsPage from './app/(dashboard)/leave/requests/page';
import LeaveCalendarPage from './app/(dashboard)/leave/calendar/page';
import LeaveLedgerPage from './app/(dashboard)/leave/ledger/page';
import DocumentTemplatesPage from './app/(dashboard)/documents/templates/page';
import DocumentBuilderPage from './app/(dashboard)/documents/templates/new/page';
import DocumentBuilderEditPage from './app/(dashboard)/documents/templates/[id]/page';
import IssueDocumentPage from './app/(dashboard)/documents/issue/page';
import IssueMaterialPage from './app/(dashboard)/documents/material/page';
import FullFinalListPage from './app/(dashboard)/documents/full-final/page';
import FullFinalWorkflowPage from './app/(dashboard)/documents/full-final/[empId]/page';
import ExpensePage from './app/(dashboard)/expense/page';
import KhataPage from './app/(dashboard)/expense/khata/page';
import ExpenseAnalyticsPage from './app/(dashboard)/expense/analytics/page';
import RecruitmentPage from './app/(dashboard)/recruitment/page';
import RecruitmentNewPage from './app/(dashboard)/recruitment/new/page';
import JobDetailPage from './app/(dashboard)/recruitment/[jobId]/page';
import JobPipelinePage from './app/(dashboard)/recruitment/[jobId]/pipeline/page';
import CompliancePage from './app/(dashboard)/compliance/page';
import ReportsPage from './app/(dashboard)/reports/page';
import ScheduledReportsPage from './app/(dashboard)/reports/scheduled/page';
import ActiveEmployeesReportPage from './app/(dashboard)/reports/active-employees/page';
import MonthlyReportPage from './app/(dashboard)/reports/monthly/page';
import SwipeReportPage from './app/(dashboard)/reports/swipe/page';
import AttendanceReportPage from './app/(dashboard)/reports/attendance/page';
import StrengthReportPage from './app/(dashboard)/reports/strength/page';
import OTReportPage from './app/(dashboard)/reports/ot/page';
import ProductsPage from './app/(dashboard)/products/page';
import PunchLogPage from './app/(dashboard)/products/punch-log/page';
import ChatPage from './app/(dashboard)/chat/page';
import ChatRoomPage from './app/(dashboard)/chat/[roomId]/page';
import BroadcastPage from './app/(dashboard)/chat/broadcast/page';
import LensPage from './app/(dashboard)/lens/page';
import VisitorsPage from './app/(dashboard)/lens/visitors/page';
import RosterPage from './app/(dashboard)/roster/page';
import SettingsPage from './app/(dashboard)/settings/page';
import BusinessSettingsPage from './app/(dashboard)/settings/business/page';
import AccountSettingsPage from './app/(dashboard)/settings/account/page';
import UsersSettingsPage from './app/(dashboard)/settings/users/page';
import RolesSettingsPage from './app/(dashboard)/settings/roles/page';
import DepartmentsSettingsPage from './app/(dashboard)/settings/departments/page';
import DesignationsSettingsPage from './app/(dashboard)/settings/designations/page';
import EmployeeFieldsSettingsPage from './app/(dashboard)/settings/employee-fields/page';
import LeavePoliciesSettingsPage from './app/(dashboard)/settings/leave-policies/page';
import HolidaysSettingsPage from './app/(dashboard)/settings/holidays/page';
import SalaryComponentsSettingsPage from './app/(dashboard)/settings/salary-components/page';
import AttendancePoliciesSettingsPage from './app/(dashboard)/settings/attendance-policies/page';
import PayslipTemplatesSettingsPage from './app/(dashboard)/settings/payslip-templates/page';
import GeofencingSettingsPage from './app/(dashboard)/settings/geofencing/page';
import ShiftsSettingsPage from './app/(dashboard)/settings/shifts/page';
import PrivacySettingsPage from './app/(dashboard)/settings/privacy/page';
import HelpSettingsPage from './app/(dashboard)/settings/help/page';
import PlanSettingsPage from './app/(dashboard)/settings/plan/page';
import PayrollBankSettingsPage from './app/(dashboard)/settings/payroll-bank/page';

// Super Admin console (role-gated below)
import SuperAdminOverviewPage from './app/(dashboard)/super-admin/page';
import SuperAdminBasicUsersPage from './app/(dashboard)/super-admin/basic-users/page';
import SuperAdminProRequestsPage from './app/(dashboard)/super-admin/pro-requests/page';
import SuperAdminCustomRequestsPage from './app/(dashboard)/super-admin/custom-requests/page';
import SuperAdminCustomCompaniesPage from './app/(dashboard)/super-admin/custom-companies/page';
import SuperAdminPlanManagementPage from './app/(dashboard)/super-admin/plan-management/page';

function App() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectUser);
  const themeMode = useSelector(selectThemeMode);
  const isSuperAdmin = isAuthenticated && user?.role === 'SUPER_ADMIN';
  // The access token lives in Redux memory only (XSS-safe) and is wiped on
  // every browser refresh — but the httpOnly refresh_token cookie survives.
  // Silently exchange it for a fresh session on first load so a refresh
  // never forces a logout while that cookie is still valid.
  const [bootstrapping, setBootstrapping] = useState(true);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', themeMode === 'dark');
  }, [themeMode]);

  useEffect(() => {
    if (isAuthenticated) { setBootstrapping(false); return; }
    authApi.post('/v1/auth/refresh')
      .then((res) => {
        const data = res.data?.data;
        if (!data?.access_token) return;
        dispatch(setAuth({ user: data.user, companyId: data.company.id, accessToken: data.access_token, permissions: data.permissions }));
        dispatch(setCompany(data.company));
        if (data.owned_companies?.length) {
          dispatch(setOwnedCompanies(data.owned_companies));
          dispatch(setActiveCompanyId(data.company.id));
        }
      })
      .catch(() => { /* no valid session — proceed to /login as normal */ })
      .finally(() => setBootstrapping(false));
    // Intentionally run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (bootstrapping) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-[var(--bg-app)]">
        <div className="w-9 h-9 border-[3px] border-[var(--accent)]/20 border-t-[var(--accent)] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/select-plan" element={isAuthenticated ? <SelectPlanPage /> : <Navigate to="/login" replace />} />
        </Route>

        <Route
          element={isAuthenticated ? <DashboardLayout /> : <Navigate to="/login" replace />}
        >
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/employees" element={<EmployeesPage />} />
          <Route path="/employees/new" element={<EmployeesNewPage />} />
          <Route path="/employees/id-cards" element={<IdCardsPage />} />
          <Route path="/employees/:id" element={<EmployeeDetailPage />} />
          <Route path="/employees/:id/edit" element={<EmployeeEditPage />} />
          <Route path="/attendance" element={<AttendancePage />} />
          <Route path="/attendance/daily" element={<AttendanceDailyPage />} />
          <Route path="/attendance/monthly" element={<AttendanceMonthlyPage />} />
          <Route path="/attendance/qr" element={<AttendanceQrPage />} />
          <Route path="/payroll" element={<PayrollPage />} />
          <Route path="/payroll/run" element={<PayrollRunPage />} />
          <Route path="/payroll/history" element={<PayrollHistoryPage />} />
          <Route path="/payroll/:runId" element={<PayrollRunDetailPage />} />
          <Route path="/payroll/salary-structures" element={<SalaryStructuresPage />} />
          <Route path="/payroll/advances" element={<AdvancesPage />} />
          <Route path="/leave" element={<LeavePage />} />
          <Route path="/leave/requests" element={<LeaveRequestsPage />} />
          <Route path="/leave/calendar" element={<LeaveCalendarPage />} />
          <Route path="/leave/ledger" element={<LeaveLedgerPage />} />
          <Route path="/documents/templates" element={<DocumentTemplatesPage />} />
          <Route path="/documents/templates/new" element={<DocumentBuilderPage />} />
          <Route path="/documents/templates/:id" element={<DocumentBuilderEditPage />} />
          <Route path="/documents/issue" element={<IssueDocumentPage />} />
          <Route path="/documents/material" element={<IssueMaterialPage />} />
          <Route path="/documents/full-final" element={<FullFinalListPage />} />
          <Route path="/documents/full-final/:empId" element={<FullFinalWorkflowPage />} />
          <Route path="/expense" element={<ExpensePage />} />
          <Route path="/expense/khata" element={<KhataPage />} />
          <Route path="/expense/analytics" element={<ExpenseAnalyticsPage />} />
          <Route path="/recruitment" element={<RecruitmentPage />} />
          <Route path="/recruitment/new" element={<RecruitmentNewPage />} />
          <Route path="/recruitment/:jobId" element={<JobDetailPage />} />
          <Route path="/recruitment/:jobId/pipeline" element={<JobPipelinePage />} />
          <Route path="/compliance" element={<CompliancePage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/reports/scheduled" element={<ScheduledReportsPage />} />
          <Route path="/reports/active-employees" element={<ActiveEmployeesReportPage />} />
          <Route path="/reports/monthly" element={<MonthlyReportPage />} />
          <Route path="/reports/swipe" element={<SwipeReportPage />} />
          <Route path="/reports/attendance" element={<AttendanceReportPage />} />
          <Route path="/reports/strength" element={<StrengthReportPage />} />
          <Route path="/reports/ot" element={<OTReportPage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/products/punch-log" element={<PunchLogPage />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/chat/:roomId" element={<ChatRoomPage />} />
          <Route path="/chat/broadcast" element={<BroadcastPage />} />
          <Route path="/lens" element={<LensPage />} />
          <Route path="/lens/visitors" element={<VisitorsPage />} />
          <Route path="/roster" element={<RosterPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/business" element={<BusinessSettingsPage />} />
          <Route path="/settings/account" element={<AccountSettingsPage />} />
          <Route path="/settings/users" element={<UsersSettingsPage />} />
          <Route path="/settings/roles" element={<RolesSettingsPage />} />
          <Route path="/settings/departments" element={<DepartmentsSettingsPage />} />
          <Route path="/settings/designations" element={<DesignationsSettingsPage />} />
          <Route path="/settings/employee-fields" element={<EmployeeFieldsSettingsPage />} />
          <Route path="/settings/leave-policies" element={<LeavePoliciesSettingsPage />} />
          <Route path="/settings/holidays" element={<HolidaysSettingsPage />} />
          <Route path="/settings/salary-components" element={<SalaryComponentsSettingsPage />} />
          <Route path="/settings/attendance-policies" element={<AttendancePoliciesSettingsPage />} />
          <Route path="/settings/payslip-templates" element={<PayslipTemplatesSettingsPage />} />
          <Route path="/settings/geofencing" element={<GeofencingSettingsPage />} />
          <Route path="/settings/shifts" element={<ShiftsSettingsPage />} />
          <Route path="/settings/privacy" element={<PrivacySettingsPage />} />
          <Route path="/settings/help" element={<HelpSettingsPage />} />
          <Route path="/settings/plan" element={<PlanSettingsPage />} />
          <Route path="/settings/payroll-bank" element={<PayrollBankSettingsPage />} />
        </Route>

        {/* Super Admin console — gated to role === SUPER_ADMIN, redirects everyone else to /dashboard */}
        <Route element={isSuperAdmin ? <DashboardLayout /> : <Navigate to="/dashboard" replace />}>
          <Route path="/super-admin" element={<SuperAdminOverviewPage />} />
          <Route path="/super-admin/basic-users" element={<SuperAdminBasicUsersPage />} />
          <Route path="/super-admin/pro-requests" element={<SuperAdminProRequestsPage />} />
          <Route path="/super-admin/custom-requests" element={<SuperAdminCustomRequestsPage />} />
          <Route path="/super-admin/custom-companies" element={<SuperAdminCustomCompaniesPage />} />
          <Route path="/super-admin/plan-management" element={<SuperAdminPlanManagementPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
